import { BRAND } from '@nt2/engine';
import { el } from './dom.js';
import { openModal } from './modal.js';
import { toast } from './toast.js';

/**
 * Who is answerable for the data, and how to reach them.
 *
 * UK and EU law require a notice to name its controller and give a contact
 * route. The route here is a relay rather than a published address: nothing on
 * the page can be scraped and turned into spam, and somebody writing in only
 * has to give an email address if they want a reply.
 *
 * The relay key is read from the environment, not committed. It is visible in
 * the built page either way — that is unavoidable for a browser form — but a
 * key that lives in one `.env.local` can be rotated without a commit, and does
 * not travel to anybody who clones the repository.
 */
export const CONTROLLER = {
  /**
   * The same name Gosco is published under ("© 2026 Why Technology"), so both
   * games on mathsifier.com answer to one publisher. Gosco describes it as a
   * pseudonym; the stricter reading of "identity of the controller" is a
   * person or a company, which is worth a moment's thought before this is
   * promoted anywhere.
   */
  name: 'Why Technology',
  country: 'the United Kingdom',
  updated: '2026-09-24',
  endpoint: 'https://api.web3forms.com/submit',
  key: (import.meta.env['VITE_CONTACT_KEY'] as string | undefined) ?? '',
} as const;

/** True once there is a real way to reach somebody. */
export const contactConfigured = (): boolean =>
  CONTROLLER.name.length > 0 && CONTROLLER.key.length > 0;

export type ContactTopic = 'Feedback' | 'Privacy request' | 'Something else';

/**
 * Get in touch.
 *
 * Every message is tagged with this game's own subject, sender name and
 * hostname, so one arriving in an inbox shared with other sites is
 * unmistakable — and filterable — without anybody having to read it first.
 */
export function openContact(topic: ContactTopic = 'Feedback'): void {
  if (!contactConfigured()) {
    openModal({
      title: 'Get in touch',
      body: el('p', {
        style: 'margin:0;color:var(--text-1)',
        text: 'This game has not been given a contact route yet, so there is ' +
              'nowhere for this to go. If you are running it, set ' +
              'VITE_CONTACT_KEY and rebuild.',
      }),
      actions: [{ label: 'Close', kind: 'primary', onClick: () => true }],
    });
    return;
  }

  let chosen: ContactTopic = topic;
  const body = el('div');
  const status = el('div', { class: 'setting__hint', style: 'margin-top:10px' });

  const select = el('select', { class: 'input' }) as HTMLSelectElement;
  for (const t of ['Feedback', 'Privacy request', 'Something else'] as const) {
    select.append(el('option', { value: t, text: t, selected: t === topic }));
  }
  select.addEventListener('change', () => { chosen = select.value as ContactTopic; });

  const email = el('input', {
    class: 'input', type: 'email', placeholder: 'you@example.com',
    autocomplete: 'off', autocorrect: 'off', spellcheck: false,
  }) as HTMLInputElement;

  const message = el('textarea', {
    class: 'input', rows: 5,
    placeholder: 'What happened, or what you would like done?',
    style: 'resize:vertical',
  }) as HTMLTextAreaElement;

  // The relay discards anything that fills this in. A person never sees it.
  const honey = el('input', {
    type: 'checkbox', tabIndex: -1, 'aria-hidden': 'true',
    style: 'position:absolute;left:-9999px',
  }) as HTMLInputElement;

  body.append(
    el('p', {
      style: 'margin:0 0 12px;color:var(--text-1)',
      text: `Messages reach ${CONTROLLER.name} directly. An email address is ` +
            'only needed if you would like a reply.',
    }),
    el('div', { class: 'field' },
      el('label', { text: 'What is this about?' }), select),
    el('div', { style: 'height:12px' }),
    el('div', { class: 'field' },
      el('label', { text: 'Your email (optional)' }), email,
      el('span', { class: 'hint', text: 'Only ever used to reply to you.' })),
    el('div', { style: 'height:12px' }),
    el('div', { class: 'field' }, el('label', { text: 'Message' }), message),
    honey,
    status,
  );

  openModal({
    title: 'Get in touch',
    body,
    actions: [{
      label: 'Send',
      kind: 'primary',
      onClick: () => {
        if (!message.value.trim()) {
          status.textContent = 'A message would help.';
          status.style.color = 'var(--bad)';
          message.focus();
          return false;                     // keep the dialog open
        }
        void send({
          topic: chosen,
          email: email.value.trim(),
          message: message.value.trim(),
          botcheck: honey.checked,
        });
        return true;
      },
    }],
  });
}

async function send(payload: {
  topic: ContactTopic; email: string; message: string; botcheck: boolean;
}): Promise<void> {
  try {
    const res = await fetch(CONTROLLER.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: CONTROLLER.key,
        // The three fields that make this recognisable at a glance.
        subject: `${BRAND.name} — ${payload.topic}`,
        from_name: `${BRAND.name}`,
        source: location.host,
        topic: payload.topic,
        email: payload.email || undefined,
        message: payload.message,
        botcheck: payload.botcheck,
      }),
    });
    const ok = res.ok && ((await res.json()) as { success?: boolean }).success === true;
    toast(ok ? 'Message sent — thank you.' : 'That did not send. Please try again.',
      ok ? 'good' : 'bad', { ms: 4000 });
  } catch {
    // Offline, or the relay is down. Never a silent failure.
    toast('That did not send — you may be offline.', 'bad', { ms: 4000 });
  }
}
